import { useState } from "react";
import Layout from "@/components/feature/Layout";
import useSeo from "@/hooks/useSeo";

const values = [
  { icon: "ri-heart-3-line", title: "热爱创作", desc: "我们始终相信好的漫画能跨越屏幕传递温度与感动。" },
  { icon: "ri-award-line", title: "品质至上", desc: "高清画质、流畅翻页、稳定体验，是我们对每一部作品的基本要求。" },
  { icon: "ri-shield-star-line", title: "版权尊重", desc: "尊重创作者与版权方，坚持正版合作，守护漫画行业的健康发展。" },
  { icon: "ri-team-line", title: "用户为本", desc: "阅读设置自由调节，倾听用户声音，持续打磨追漫体验。" },
];

const milestones = [
  { year: "2019", text: "漫悦漫画网正式上线，首批 50 部正版漫画入驻。" },
  { year: "2021", text: "用户突破 300 万，上线漫画夜间模式与翻页阅读器。" },
  { year: "2023", text: "推出横屏阅读与缓存下载，离线追漫成为现实。" },
  { year: "2026", text: "注册用户超过 1200 万，累计阅读量突破 30 亿次。" },
];

const reviews = [
  { name: "追漫小透明", content: "翻页模式太丝滑了，画质也很清晰，夜间模式深夜追漫特别舒服！", rating: 5 },
  { name: "二刺猿老白", content: "横屏阅读和缓存下载简直是通勤神器，地铁上离线也能看，太赞了。", rating: 5 },
  { name: "奶茶配漫画", content: "分类清晰，更新也及时，阅读设置能自由调节，体验非常棒。", rating: 4 },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "漫悦漫画网",
  url: "/about",
  description: "专业的在线漫画阅读平台，支持翻页模式、横屏阅读、夜间模式与缓存下载。",
  foundingDate: "2019",
  address: {
    "@type": "PostalAddress",
    streetAddress: "南京西路漫画创意园区",
    addressLocality: "上海",
    addressRegion: "上海市",
    postalCode: "200041",
    addressCountry: "CN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "400-820-2026",
    contactType: "customer service",
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "/" },
    { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
  ],
};

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useSeo({
    title: "关于我们 - 品牌介绍·口碑·联系方式",
    description:
      "漫悦漫画网成立于 2019 年，是专业的在线漫画阅读平台。了解我们的品牌故事、发展历程、用户口碑与联系方式，支持翻页模式、横屏阅读、夜间模式与缓存下载。",
    keywords: "漫悦漫画网,关于我们,漫画平台,漫画阅读,联系我们",
    path: "/about",
    jsonLd: [aboutSchema, breadcrumbSchema],
  });

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("company_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("ok");
      setSuccessMsg("感谢您的留言，我们会尽快回复。");
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const clean = new FormData(form);
      clean.delete("company_alt");
      const res = await fetch("https://readdy.ai/api/form/da08p66th95mubjtue3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(clean as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore parse error */
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("ok");
        setSuccessMsg(serverMsg || "提交成功，我们会尽快与您联系！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative flex min-h-[360px] items-center overflow-hidden bg-background-950">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20manga%20style%20collage%20representing%20a%20comic%20publishing%20team%20with%20sketches%20ink%20pens%20drawing%20tablets%20and%20storyboards%2C%20warm%20orange%20and%20amber%20lighting%20on%20dark%20background%2C%20creative%20studio%20atmosphere%2C%20high%20detail%20illustration&width=1600&height=800&seq=manga-about-01&orientation=landscape"
          alt="漫悦漫画网创作团队"
          title="关于我们 漫画平台"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/60 to-background-950/85" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-500/20 px-3 py-1 text-xs font-medium text-accent-500 ring-1 ring-accent-500/40">
              <i className="ri-quill-pen-line" aria-hidden="true" />
              关于我们
            </p>
            <h1 className="font-heading text-3xl leading-tight text-background-50 md:text-4xl">
              让每一部好漫画
              <br />
              <span className="text-accent-500">都被用心阅读</span>
            </h1>
          </div>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">品牌故事</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">关于漫悦漫画网</h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              漫悦漫画网成立于 2019 年，总部位于上海，是一家专注正版漫画内容与阅读体验的在线漫画平台。我们致力于为每一位漫画爱好者提供翻页流畅、画质清晰、支持夜间模式与缓存下载的优质追漫体验。七年来，我们携手众多创作者与版权方，将一部部动人的漫画作品带给数千万读者。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              从漫画翻页模式到横屏阅读，从护眼夜间模式到离线缓存下载，我们始终坚持用技术打磨阅读体验，让阅读设置自由调节，为追漫体验加分。
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${v.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 发展历程 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">发展历程</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">一路走来的足迹</h2>
          </div>
          <div className="relative space-y-8 border-l-2 border-background-200 pl-6">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-background-50" />
                </span>
                <span className="font-heading text-lg text-primary-600">{m.year}</span>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑评价 */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">用户口碑</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">被千万读者喜爱</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex gap-1 text-accent-500" aria-label={`${r.rating} 星评价`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={i < r.rating ? "ri-star-fill" : "ri-star-line"} aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{r.content}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-2 text-sm font-medium text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-user-line" aria-hidden="true" />
                  </span>
                  {r.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 联系方式 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-primary-600">联系我们</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">期待听到你的声音</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                无论是建议、反馈还是合作洽谈，欢迎通过以下方式联系我们，我们会尽快回复。
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">公司地址</p>
                    <p className="mt-1 text-sm text-foreground-600">上海市静安区南京西路漫画创意园区 2 号楼 8 层</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                    <p className="mt-1 text-sm text-foreground-600">service@manyue-manga.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-phone-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">客服热线</p>
                    <p className="mt-1 text-sm text-foreground-600">400-820-2026（工作日 9:00-18:00）</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-time-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">营业时间</p>
                    <p className="mt-1 text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              {/* 地图 */}
              <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
                <iframe
                  title="漫悦漫画网位置地图"
                  src="https://www.google.com/maps?q=Shanghai%20Jing%27an%20District%20Nanjing%20West%20Road&output=embed"
                  className="h-56 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* 留言表单 */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-sm text-foreground-600">填写下方表单，我们会尽快回复你的问题。</p>

              <form id="contact-form" data-readdy-form onSubmit={submit} className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">姓名</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      maxLength={30}
                      placeholder="请输入姓名"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={60}
                      placeholder="请输入邮箱"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">主题</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    maxLength={50}
                    placeholder="请输入留言主题"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请输入留言内容（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  data-honeypot="true"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin" aria-hidden="true" />
                      提交中…
                    </>
                  ) : (
                    "提交留言"
                  )}
                </button>

                {status === "ok" && (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-line" aria-hidden="true" />
                    {successMsg}
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
                    <i className="ri-error-warning-line" aria-hidden="true" />
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}